import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Upload, FileText, Image, Video, FileSpreadsheet, 
  Download, Trash2, Eye, X, Loader2 
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { showToast } from '../utils/toast';
import { formatDateEST } from '../utils/timezone';

const API = process.env.REACT_APP_BACKEND_URL || '';

export default function ClientAssets() {
  const navigate = useNavigate();
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [assetType, setAssetType] = useState('Documents');
  const [notes, setNotes] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    fetchAssets();
  }, []);

  const fetchAssets = async () => {
    try {
      const response = await fetch(`${API}/api/client/me`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setAssets(data.assets || []);
      } else if (response.status === 401) {
        navigate('/client/login');
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
      showToast({
        title: 'Error',
        message: 'Failed to load assets',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Check file size (10MB limit)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      showToast({
        title: 'File too large',
        message: 'Maximum file size is 10MB',
        type: 'error'
      });
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showToast({
        title: 'No file selected',
        message: 'Please select a file to upload',
        type: 'error'
      });
      return;
    }

    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('asset_type', assetType);
      if (notes) {
        formData.append('notes', notes);
      }

      const response = await fetch(`${API}/api/client/assets/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData
      });

      if (response.ok) {
        showToast({
          title: 'Success',
          message: 'File uploaded successfully',
          type: 'success'
        });
        setShowUploadModal(false);
        setSelectedFile(null);
        setNotes('');
        setAssetType('Documents');
        await fetchAssets();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      showToast({
        title: 'Upload failed',
        message: error.message || 'Failed to upload file',
        type: 'error'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;

    try {
      const response = await fetch(`${API}/api/client/assets/${assetId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        showToast({
          title: 'Deleted',
          message: 'Asset deleted successfully',
          type: 'success'
        });
        await fetchAssets();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Delete failed');
      }
    } catch (error) {
      console.error('Error deleting asset:', error);
      showToast({
        title: 'Delete failed',
        message: error.message || 'Failed to delete asset',
        type: 'error'
      });
    }
  };

  const getAssetIcon = (extension) => {
    const imageExts = ['.png', '.jpg', '.jpeg', '.gif', '.webp'];
    const videoExts = ['.mp4', '.mov', '.avi'];
    const docExts = ['.pdf', '.doc', '.docx'];
    const spreadsheetExts = ['.xls', '.xlsx', '.csv'];

    if (imageExts.includes(extension)) return <Image className="w-5 h-5" />;
    if (videoExts.includes(extension)) return <Video className="w-5 h-5" />;
    if (spreadsheetExts.includes(extension)) return <FileSpreadsheet className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  const groupAssetsByType = () => {
    const grouped = {
      'Images': [],
      'Videos': [],
      'Documents': [],
      'Spreadsheets': [],
      'Other': []
    };

    assets.forEach(asset => {
      const ext = asset.file_extension?.toLowerCase() || '';
      if (['.png', '.jpg', '.jpeg', '.gif', '.webp'].includes(ext)) {
        grouped['Images'].push(asset);
      } else if (['.mp4', '.mov', '.avi'].includes(ext)) {
        grouped['Videos'].push(asset);
      } else if (['.pdf', '.doc', '.docx'].includes(ext)) {
        grouped['Documents'].push(asset);
      } else if (['.xls', '.xlsx', '.csv'].includes(ext)) {
        grouped['Spreadsheets'].push(asset);
      } else if (asset.type) {
        if (!grouped[asset.type]) grouped[asset.type] = [];
        grouped[asset.type].push(asset);
      } else {
        grouped['Other'].push(asset);
      }
    });

    return grouped;
  };

  const groupedAssets = groupAssetsByType();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/client-dashboard')}
            variant="ghost"
            className="mb-4 text-blue-300 hover:text-blue-200 hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">
                My Assets
              </h1>
              <p className="text-blue-200">
                Upload and manage your project files and documents
              </p>
            </div>

            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-lg"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          </div>
        </div>

        {/* Assets Grid by Type */}
        {Object.entries(groupedAssets).map(([type, typeAssets]) => {
          if (typeAssets.length === 0) return null;

          return (
            <div key={type} className="mb-8">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                {type}
                <span className="ml-2 px-2 py-0.5 bg-blue-500/20 text-blue-300 text-sm rounded-full">
                  {typeAssets.length}
                </span>
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {typeAssets.map(asset => (
                  <div
                    key={asset.asset_id}
                    className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-4 hover:bg-white/15 transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-500/20 rounded-lg text-blue-300">
                          {getAssetIcon(asset.file_extension)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-white font-medium truncate">
                            {asset.name}
                          </h3>
                          <p className="text-xs text-blue-200">
                            Uploaded by {asset.uploaded_by_name}
                          </p>
                        </div>
                      </div>
                    </div>

                    {asset.notes && (
                      <p className="text-sm text-blue-200 mb-3">
                        {asset.notes}
                      </p>
                    )}

                    <div className="flex items-center justify-between text-xs text-blue-300">
                      <span>
                        {formatDateEST(asset.uploaded_at)}
                      </span>
                      <div className="flex space-x-2">
                        <a
                          href={`${API}${asset.file_url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1.5 hover:bg-blue-500/20 rounded transition-colors"
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                        {asset.uploaded_by === 'client' && (
                          <button
                            onClick={() => handleDelete(asset.asset_id)}
                            className="p-1.5 hover:bg-red-500/20 text-red-400 rounded transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {assets.length === 0 && (
          <div className="backdrop-blur-xl bg-white/10 rounded-xl border border-white/20 p-12 text-center">
            <Upload className="w-16 h-16 text-blue-300 mx-auto mb-4 opacity-50" />
            <h3 className="text-xl font-semibold text-white mb-2">
              No assets yet
            </h3>
            <p className="text-blue-200 mb-6">
              Upload your first file to get started
            </p>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload File
            </Button>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-slate-900 to-blue-900 rounded-2xl border border-white/20 p-6 max-w-lg w-full">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">Upload File</h2>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setSelectedFile(null);
                  setNotes('');
                }}
                className="text-blue-300 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* File Input */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Select File (Max 10MB)
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept=".png,.jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.zip,.mp4"
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                />
                {selectedFile && (
                  <p className="mt-2 text-sm text-green-400">
                    Selected: {selectedFile.name}
                  </p>
                )}
              </div>

              {/* Asset Type */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Category
                </label>
                <select
                  value={assetType}
                  onChange={(e) => setAssetType(e.target.value)}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Documents">Documents</option>
                  <option value="Images">Images</option>
                  <option value="Videos">Videos</option>
                  <option value="Spreadsheets">Spreadsheets</option>
                  <option value="Designs">Designs</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              {/* Notes */}
              <div>
                <label className="block text-sm font-medium text-blue-200 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add any notes about this file..."
                  rows={3}
                  className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-blue-300/50 focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Upload Button */}
              <div className="flex space-x-3 pt-4">
                <Button
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setNotes('');
                  }}
                  variant="outline"
                  className="flex-1 border-white/20 text-white hover:bg-white/10"
                  disabled={uploading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white"
                  disabled={uploading || !selectedFile}
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
